import { useEffect, useMemo, useState } from 'react';
import { KoobButton } from '../../components';
import { WizardOverlay } from './overlay';
import { StepList } from './StepList';
import { answeredStepIds, buildSteps } from './steps';
import { useTripWizard } from './useTripWizard';
import { WizardShell } from './WizardShell';
import type { EntryChoice, TripWizardConfig, WizardValues } from './types';

export interface TripDesignerWizardProps {
  /** `create` = step-by-step wizard; `edit` = all fields on one screen. */
  mode: 'create' | 'edit';
  config: TripWizardConfig;
  /** Seed values (edit / duplicate). */
  initialValues?: Partial<WizardValues>;
  /** Existing trip id — sent as `id` on save in edit mode. */
  editId?: string;
  /** Tiles on the entry screen (create mode). No `onSelect` = starts the flow. */
  entryChoices?: EntryChoice[];
  /** Skip the entry screen and open on the first step (create mode). */
  startImmediately?: boolean;
  /**
   * Create mode: steps whose seeded answer can't be changed (e.g. a trip
   * designed from a template keeps its countries and DMC). Left out of the
   * flow when the seed answers them, shown read-only on the review.
   */
  fixedStepIds?: string[];
}

export function TripDesignerWizard({
  mode,
  config,
  initialValues,
  editId,
  entryChoices,
  startImmediately,
  fixedStepIds,
}: TripDesignerWizardProps) {
  const { values, set, patch, reset, submit, submitting, error } =
    useTripWizard({
      config,
      seed: initialValues,
      editId,
    });
  // Settled once, on the seed: a step asked because the seed left it
  // unanswered must not vanish from the flow as soon as it gets an answer.
  const [skipStepIds] = useState(() =>
    mode === 'create' && fixedStepIds?.length
      ? answeredStepIds(config, fixedStepIds, values)
      : [],
  );
  // Solo traveller: no room split to ask about — the step is dropped.
  const skipRooms =
    values.adultPax === 1 && values.childrenBirthdates.length === 0;
  const steps = useMemo(
    () =>
      buildSteps(config, {
        mode,
        hasBrief: !!values.brief,
        skipRooms,
        skipStepIds,
      }),
    [config, mode, values.brief, skipRooms, skipStepIds],
  );
  // The hidden step no longer validates the split, so collapse any stale
  // multi-room composition left over from before the pax dropped to 1.
  const soloRoomStale =
    skipRooms &&
    (values.rooms.length !== 1 ||
      values.rooms[0].adults !== 1 ||
      values.rooms[0].children !== 0);
  useEffect(() => {
    if (soloRoomStale) set('rooms', [{ adults: 1, children: 0 }]);
  }, [soloRoomStale, set]);
  const { t } = config;

  if (mode === 'edit') {
    const allValid = steps.every((s) => s.validate(values));
    return (
      <WizardOverlay config={config}>
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          <div className="mx-auto w-full max-w-2xl">
            <h1 className="m-0 text-2xl font-semibold text-gray-900">
              {t('editHeader')}
            </h1>
            {values.name && (
              <p className="mt-1 text-sm text-gray-400">{values.name}</p>
            )}
            <div className="mt-4">
              <StepList
                steps={steps}
                values={values}
                set={set}
                patch={patch}
                config={config}
                lockedStepIds={['countries', 'dmc']}
              />
            </div>
            {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            className="border-none bg-transparent text-sm text-gray-500 hover:text-gray-800"
            onClick={() => config.onExit?.()}
            disabled={!config.onExit}
          >
            ← {t('back')}
          </button>
          <KoobButton
            variant="primary"
            loading={submitting}
            disabled={!allValid}
            onClick={submit}
          >
            {t('summary.save')}
          </KoobButton>
        </div>
      </WizardOverlay>
    );
  }

  return (
    <WizardShell
      config={config}
      steps={steps}
      values={values}
      set={set}
      patch={patch}
      submit={submit}
      reset={reset}
      submitting={submitting}
      error={error}
      entryChoices={entryChoices}
      startImmediately={startImmediately}
    />
  );
}
